import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n/lib';
import { isSchoolOrTeacher, showPriceWithVat } from '../../utilities';
import IconLazy from '../_helpers/Icon/IconLazy';

const Wrapper = styled.div<{ size?: string }>`
  background: ${({ theme, size }) =>
    size === 'extra-small' ? 'transparent' : theme.color.white};
  padding: ${({ size }) =>
    size === 'extra-small' ? '0' : `${rem(10)} ${rem(20)}`};
  margin-top: ${({ size }) => (size === 'extra-small' ? '0' : rem(2))};
  box-shadow: ${({ theme, size }) =>
    size === 'extra-small' ? 'none' : theme.boxShadow.small};
  ${({ theme, size }) => theme.media('sm')} {
    border-radius: ${({ theme, size }) =>
      size === 'extra-small' ? '0' : rem(theme.borderRadius.default)};
    padding: ${({ size }) =>
      size === 'small'
        ? `${rem(10)} ${rem(20)} ${rem(20)} ${rem(20)}`
        : size === 'extra-small'
        ? '0'
        : `0 ${rem(20)} ${rem(20)} ${rem(20)}`};
    margin-top: 0;
  }
`;

const Text = styled.div<{ size?: string }>`
  font-size: ${rem(14)};
  text-align: center;
  margin: 0 0 ${rem(8)};
  ${({ theme, size }) => theme.media('sm')} {
    margin: ${({ size }) =>
      size === 'small' ? `${rem(0)} ${rem(0)} ${rem(8)} ${rem(0)}` : `0`};
  }
`;

const TextGroup = styled.div`
  margin: 0;
  display: inline-block;
  vertical-align: middle;
  max-width: ${rem(190)};
  ${({ theme }) => theme.media('xs')} {
    max-width: initial;
  }
`;

const TextGreen = styled(TextGroup)`
  font-weight: bold;
  vertical-align: initial;
  color: ${({ theme }) => theme.color.success};
`;

const TextRed = styled(TextGroup)`
  vertical-align: initial;
  color: ${({ theme }) => theme.color.error};
`;

const ShippingIcon = styled(IconLazy)<{ size?: string }>`
  ${({ theme, size }) => (size === 'small' ? theme.size(40) : theme.size(58))};
  margin-right: ${({ size }) => (size === 'extra-small' ? '0' : rem(10))};
  ${({ theme }) => theme.transition('fill')};
  &.success {
    fill: ${({ theme }) => theme.color.success};
  }
  &.error {
    fill: ${({ theme }) => theme.color.error};
  }
`;

const TopRow = styled.div<{ size?: string }>`
  ${({ size }) =>
    size === 'extra-small' &&
    `
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: ${rem(8)};
  `}
`;

const IconBox = styled.div<{ size?: string }>`
  ${({ size }) =>
    size === 'extra-small' &&
    `
    width: ${rem(60)};
    display: flex;
    justify-content: center;
    margin-right: ${rem(8)};
  `}
`;

const TextExtraSmall = styled(Text)<{ size?: string }>`
  ${({ size }) =>
    size === 'extra-small' &&
    `
    text-align: left;
    margin: 0;
    display: flex;
    align-items: center;
    font-size: ${rem(14)};
    line-height: ${rem(18)};
  `}
`;

const Range = styled.div`
  background: ${({ theme }) => theme.color.gray93};
  border-radius: ${rem(2)};
  height: ${rem(4)};
  position: relative;
  overflow: hidden;
`;

const RangeFilled = styled.div`
  border-radius: ${rem(2)};
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  ${({ theme }) => theme.transitions('background-color, width')};
  &.success {
    background: ${({ theme }) => theme.color.success};
  }
  &.error {
    background: ${({ theme }) => theme.color.error};
  }
`;

interface CartShippingRangeProps {
  cart?: any;
  user: any;
  freeShippingPrice: number;
  className?: string;
  size?: 'small' | 'extra-small' | string;
  textType?: number;
}

class CartShippingRange extends React.PureComponent<CartShippingRangeProps> {
  public render() {
    const { freeShippingPrice, cart, user, className, size, textType } =
      this.props;
    const giftcards = cart?.giftcards;
    const showWithVat = showPriceWithVat(user);
    const price =
      showWithVat && cart?.total_items_price_with_discount
        ? cart.total_items_price_with_discount
        : cart?.total_items_price_without_vat
        ? cart.total_items_price_without_vat
        : 0;
    const isFreeShipping =
      isSchoolOrTeacher(user) ||
      price / freeShippingPrice >= 1 ||
      giftcards?.[0]?.freeshipping;

    if (size === 'extra-small') {
      return (
        <Wrapper className={className || ''} size={size}>
          <TopRow size={size}>
            <IconBox size={size}>
              <ShippingIcon
                icon="shippingBold"
                className="success"
                size={size}
              />
            </IconBox>
            <TextExtraSmall size={size}>
              {isFreeShipping ? (
                <TextGroup>
                  {__('Dopravu máte ')}
                  <strong>{__('zadarmo!')}</strong>
                </TextGroup>
              ) : (
                <TextGroup>
                  {__('Pri nákupe za ')}
                  <TextRed>
                    {__('ďalších')}
                    &nbsp;
                    <strong>{(freeShippingPrice - price).toFixed(2)} €</strong>
                  </TextRed>
                  {textType === 1 ? (
                    <>
                      <div>
                        {__(' získate ')}{' '}
                        <TextGreen>{__('dopravu zadarmo')}</TextGreen>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        {__(' získate ')} <TextGreen>{__('dopravu')}</TextGreen>
                      </div>
                      <TextGreen>{__('zadarmo.')}</TextGreen>
                    </>
                  )}
                </TextGroup>
              )}
            </TextExtraSmall>
          </TopRow>
          <Range>
            <RangeFilled
              className={isFreeShipping ? 'success' : 'error'}
              style={{
                width: isFreeShipping
                  ? '100%'
                  : (price / freeShippingPrice) * 100 + '%',
              }}
            />
          </Range>
        </Wrapper>
      );
    }

    return (
      <Wrapper className={className || ''} size={size}>
        <Text size={size}>
          <ShippingIcon icon="shippingBold" className={'success'} size={size} />
          {isFreeShipping ? (
            <TextGroup>
              {__('Dopravu máte ')}
              <strong>{__('zadarmo!')}</strong>
            </TextGroup>
          ) : (
            <TextGroup>
              {__('Pri nákupe za ')}
              <TextRed>
                {__('ďalších')}
                &nbsp;
                <strong>{(freeShippingPrice - price).toFixed(2)} €</strong>
              </TextRed>
              {__(' získate ')}
              <TextGreen>{__('dopravu zadarmo.')}</TextGreen>
            </TextGroup>
          )}
        </Text>
        <Range>
          <RangeFilled
            className={'success'}
            style={{
              width: isFreeShipping
                ? '100%'
                : (price / freeShippingPrice) * 100 + '%',
            }}
          />
        </Range>
      </Wrapper>
    );
  }
}

export default CartShippingRange;
